import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InterviewButton, TimerCountdown } from '@vette/ui-components';
import {
  InterviewerDocument,
  InterviewerFragment,
  InterviewerQuery,
  InterviewerQueryVariables,
  SessionStatusEnum,
} from '@vette/data-access';
import { firebase } from '@vette/frontend-utils';
import { date, session as sessionEntity, env } from '@vette/common-utils';
import { useApolloClient } from '@apollo/client';
import { useStatusMessage, useInterviewNowAttributes } from './hooks';

type Props = {
  submitting?: boolean;
  onSessionFinish: () => void;
};

const VETTER_PICKUP_CALL_TIMEOUT = env.checkEnvVarValue(
  process.env.NEXT_PUBLIC_VETTER_PICKUP_CALL_TIMEOUT
);

const DynamicButtonWrap: React.FC<Props> = ({
  onSessionFinish,
  submitting,
}) => {
  const timerCountdownComponent = useRef<React.ReactNode>();
  const client = useApolloClient();
  const [vetter, setVetter] = useState<InterviewerFragment | null>(null);

  const { isDisabled, sessionId, isNonInteractive, isPredemo } =
    useInterviewNowAttributes();

  const [status, loading] = firebase.database.useWatchRefState<
    SessionStatusEnum | undefined
  >(sessionId && `/sessions/${sessionId}/status`);

  useStatusMessage(
    useCallback(() => {
      timerCountdownComponent.current = undefined;
      onSessionFinish();
    }, [onSessionFinish]),
    status,
    loading,
    isPredemo
  );

  useEffect(() => {
    if (!status || !sessionId) return;

    if (
      sessionEntity.WAITING_FOR_VETTER_CONNECT_STATUSES.some(s => s === status)
    ) {
      timerCountdownComponent.current = null;
    }

    if (status === 'WAITING_FOR_VETTER_TO_PICKUP_CALL') {
      timerCountdownComponent.current = (
        <TimerCountdown
          targetTimestamp={date.utcFutureTimestamp(VETTER_PICKUP_CALL_TIMEOUT)}
        />
      );
      // refetchInterviewer({ sessionId });
      (async () => {
        const { data, errors } = await client.query<
          InterviewerQuery,
          InterviewerQueryVariables
        >({
          query: InterviewerDocument,
          variables: { sessionId },
          fetchPolicy: 'network-only',
        });
        if (data?.interviewer?.__typename === 'User') {
          return setVetter(data.interviewer);
        }
        console.error('Unable to load vetter data', { errors, data });
      })();
    }
  }, [status, loading, client, sessionId, onSessionFinish]);

  const determineButtonState = (): React.ComponentProps<
    typeof InterviewButton
  >['state'] => {
    if (vetter) return 'found-vetter';
    if (
      status === SessionStatusEnum.AI_VETTER_DIALING ||
      status === SessionStatusEnum.AI_VETTER_INTERVIEWING
    )
      return 'ai-interviewing';
    if (sessionId || submitting) return 'looking-for-vetter';
    if (isDisabled) return 'disabled';
    return 'enabled';
  };

  const buttonState = determineButtonState();
  return (
    <InterviewButton
      aria-label="Interview Now"
      isNonInteractive={isNonInteractive}
      className={buttonState}
      state={buttonState}
      vetter={
        status &&
        sessionEntity.WAITING_FOR_VETTER_CONNECT_STATUSES.some(
          s => s === status
        )
          ? null
          : vetter
      }
      timerNode={timerCountdownComponent.current}
      isPredemo={isPredemo}
    />
  );
};

export default DynamicButtonWrap;
